// src/pages/solicitudes/SolicitudesAgendadas.js
import React, { useEffect, useState } from 'react'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Table from 'react-bootstrap/Table'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import InputGroup from 'react-bootstrap/InputGroup'
import Button from 'react-bootstrap/Button'
import Badge from 'react-bootstrap/Badge'
import Alert from 'react-bootstrap/Alert'
import { toast } from 'react-toastify'
import Paginacion from 'components/Paginacion'
import api from 'api/api'

import './solicitudes.css'
import { DEFAULT_FETCH_CUANTOS } from 'utils/constants'
import { debounce } from 'utils/debounce'
import { toDisplayDateTime } from 'utils/date'
import VerSolicitud from './VerSolicitud'
import FiltrosSolicitudes from './FiltrosSolicitudes'

// Estados que corresponden a solicitudes agendadas
const ESTADOS_AGENDADAS = ['agendado']

function SolicitudesAgendadas() {
	const [fetchDeACuantos, setFetchDeACuantos] = useState(DEFAULT_FETCH_CUANTOS)
	const [numResultados, setNumResultados] = useState(0)
	const [paginaAct, setPaginaAct] = useState(1)
	const [loading, setLoading] = useState(false)

	const [busqueda, setBusqueda] = useState('')
	const [filtroBusqueda, setFiltroBusqueda] = useState('')
	const [filtroCategoria, setFiltroCategoria] = useState('')
	const [filtroCiudad, setFiltroCiudad] = useState('')
	const [filtroTipo, setFiltroTipo] = useState('')

	const [solicitudes, setSolicitudes] = useState([])
	const [solicitudActual, setSolicitudActual] = useState(null)
	const [mostrarDetalle, setMostrarDetalle] = useState(false)

	const opcionesCuantosMostrar = [10, 25, 50]

	const refreshSolicitudes = async () => {
		try {
			setLoading(true)

			const filtros = {
				estados: ESTADOS_AGENDADAS
			}

			if (filtroBusqueda) filtros.busqueda = filtroBusqueda
			if (filtroCategoria) filtros.categoriaId = filtroCategoria
			if (filtroCiudad) filtros.ciudadId = filtroCiudad
			if (filtroTipo) filtros.tipo = filtroTipo

			const { datos, error } = await api.post('a/ver-solicitudes', {
				cuantos: fetchDeACuantos,
				desde: paginaAct === 1 ? 0 : (paginaAct - 1) * fetchDeACuantos,
				filtros
			})

			if (error) {
				toast.error('Error al cargar solicitudes: ' + error)
				return
			}

			setSolicitudes(datos.solicitudes || [])
			setNumResultados(datos.numResultados || 0)
		} catch (error) {
			toast.error('Error de conexion')
			console.error(error)
		} finally {
			setLoading(false)
		}
	}

	const handleBusqueda = (value) => {
		setFiltroBusqueda(value || '')
	}

	const debouncedBusqueda = React.useCallback(
		debounce(handleBusqueda, 400),
		[]
	)

	// Efecto para debounce de busqueda
	useEffect(() => {
		debouncedBusqueda(busqueda)
	}, [busqueda, debouncedBusqueda])

	// Ref para controlar si es el primer render
	const isFirstRender = React.useRef(true)

	// Ref para evitar doble llamada al cambiar filtros
	const skipNextPaginaEffect = React.useRef(false)

	// Efecto para carga inicial
	useEffect(() => {
		refreshSolicitudes()
	}, [])

	// Efecto cuando cambia la paginacion
	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false
			return
		}

		if (skipNextPaginaEffect.current) {
			skipNextPaginaEffect.current = false
			return
		}

		refreshSolicitudes()
	}, [paginaAct])

	// Efecto cuando cambian los filtros (resetea a pagina 1 y recarga)
	useEffect(() => {
		if (isFirstRender.current) {
			return
		}

		if (paginaAct !== 1) {
			skipNextPaginaEffect.current = true
			setPaginaAct(1)
		}

		refreshSolicitudes()
	}, [filtroBusqueda, filtroCategoria, filtroCiudad, filtroTipo, fetchDeACuantos])

	const handleVerSolicitud = (solicitud) => {
		setSolicitudActual(solicitud)
		setMostrarDetalle(true)
	}

	const cerrarDetalle = () => {
		setMostrarDetalle(false)
		setSolicitudActual(null)
	}

	const renderTipoBadge = (tipo) => {
		const config = {
			urgencia: { className: 'badge-urgencia', text: 'Urgencia' },
			presupuesto: { className: 'badge-presupuesto', text: 'Presupuesto' },
			evaluacion: { className: 'badge-evaluacion', text: 'Evaluacion' }
		}
		const { className, text } = config[tipo] || { className: 'badge', text: tipo }
		return <Badge bg={'none'} className={className}>{text}</Badge>
	}

	return (
		<Container fluid id="solicitudes" className="vh-100">
			<Row className="d-flex no-gutters">
				<Col xs={12} className="mt-2">
					<h1>Solicitudes Agendadas</h1>
					<hr />
				</Col>
			</Row>

			{/* Barra de busqueda y filtros */}
			<Row className="d-flex no-gutters">
				<Col className="mt-2">
					<Form>
						<Row id="searchbar" className="justify-content-between align-items-center">
							<Col sm={6} md={2}>
								<small>
									<span className="d-none d-sm-inline-block">Mostrar de a</span>
									<Form.Group style={{ display: 'inline-block' }}>
										<Form.Select
											size="sm"
											onChange={(e) => setFetchDeACuantos(e.target.value)}
											className="mx-1"
										>
											{opcionesCuantosMostrar.map((o) => (
												<option value={o} key={`mostrar-${o}`}>{o}</option>
											))}
										</Form.Select>
									</Form.Group>
									<span className="ms-2">resultados</span>
								</small>
							</Col>
							<Col md={4} lg={3}>
								<Form.Group controlId="txtBusqueda">
									<InputGroup>
										<Form.Control
											autoFocus
											value={busqueda}
											onChange={(e) => setBusqueda(e.target.value)}
											type="text"
											placeholder="Buscar por ID, descripcion, usuario..."
											aria-label="Buscar"
										/>
										<InputGroup.Text><i className="fa fa-search"></i></InputGroup.Text>
									</InputGroup>
								</Form.Group>
							</Col>
						</Row>
					</Form>
				</Col>
			</Row>

			{/* Filtros adicionales */}
			<FiltrosSolicitudes
				filtroCategoria={filtroCategoria}
				setFiltroCategoria={setFiltroCategoria}
				filtroCiudad={filtroCiudad}
				setFiltroCiudad={setFiltroCiudad}
				filtroTipo={filtroTipo}
				setFiltroTipo={setFiltroTipo}
				mostrarFiltroEstado={false}
			/>

			{/* Lista de solicitudes */}
			<Row>
				<Col sm={12}>
					{loading ? (
						<div className="text-center my-5">
							<Spinner
								animation="border"
								size="lg"
								role="status"
								aria-hidden="true"
								variant="primary"
							/>
							<p className="mt-2">Cargando solicitudes...</p>
						</div>
					) : solicitudes.length === 0 ? (
						<Alert variant="info" className="my-4">
							No se encontraron solicitudes agendadas
						</Alert>
					) : (
						<Table id="table-solicitudes" className="table table-striped table-hover">
							<thead>
								<tr>
									<th>ID</th>
									<th>Tipo</th>
									<th>Categoria</th>
									<th>Usuario</th>
									<th>Ciudad</th>
									<th>Fecha Creacion</th>
									<th>Fecha Agendado</th>
									<th>Propuestas</th>
									<th>Acciones</th>
								</tr>
							</thead>
							<tbody>
								{solicitudes.map((solicitud) => (
									<tr key={solicitud.id}>
										<td><strong>#{solicitud.id}</strong></td>
										<td>{renderTipoBadge(solicitud.tipo)}</td>
										<td>
											<div className="d-flex align-items-center">
												{solicitud.categoria.imagen2Url && (
													<img
														src={process.env.REACT_APP_API_URL + 'uploads/' + solicitud.categoria.imagen2Url}
														alt=""
														className="categoria-icon me-2"
													/>
												)}
												<span>{solicitud.categoria.nombre}</span>
											</div>
										</td>
										<td>
											<div>
												<span>{solicitud.usuario.nombre}</span>
												{solicitud.usuario.telefono && (
													<small className="d-block text-muted">{solicitud.usuario.telefono}</small>
												)}
											</div>
										</td>
										<td>{solicitud.ciudad || '-'}</td>
										<td>{toDisplayDateTime(solicitud.fechaCreacion)}</td>
										<td>
											<Badge bg="success">
												{toDisplayDateTime(solicitud.fechaAgendado)}
											</Badge>
										</td>
										<td>
											<Badge bg={solicitud.cantidadPropuestas > 0 ? 'success' : 'secondary'}>
												{solicitud.cantidadPropuestas}
											</Badge>
										</td>
										<td>
											<Button
												variant="outline-primary"
												size="sm"
												onClick={() => handleVerSolicitud(solicitud)}
											>
												<i className="fa fa-eye me-1"></i> Ver
											</Button>
										</td>
									</tr>
								))}
							</tbody>
						</Table>
					)}
					<Col>
						<Paginacion
							numResultados={numResultados}
							pagina={paginaAct}
							setPagina={setPaginaAct}
							deACuantos={fetchDeACuantos}
						/>
					</Col>
				</Col>
			</Row>

			{/* Panel de detalle */}
			{mostrarDetalle && solicitudActual && (
				<VerSolicitud
					solicitudId={solicitudActual.id}
					onClose={cerrarDetalle}
				/>
			)}
		</Container>
	)
}

export default SolicitudesAgendadas